import { useRef, useState, type RefObject } from 'react';
import { IonAlert, IonButton, IonContent, IonHeader, IonItem, IonLabel, IonList, IonNav, IonNote, IonPage, IonText, IonTitle, IonToolbar } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { TransitPolicyRule } from '../models/TransitPolicy';
import { EventClassification, EventTriggerSource } from '../models/DeviceEvent';
import { FlapState, MotionSensorState } from '../models/FrameMetadata';
import { EditDoorPolicySoundPickerView, getSoundLabel } from './edit-door-policy/EditDoorPolicySoundPicker';
import { EditDoorPolicyLockPickerView, type LockActionOption, type LockDurationOption } from './edit-door-policy/EditDoorPolicyLockPicker';
import { EditDoorPolicyTriggerSourcePickerView } from './edit-door-policy/EditDoorPolicyTriggerSourcePicker';
import { EditDoorPolicyClassificationPickerView } from './edit-door-policy/EditDoorPolicyClassificationPicker';
import { EditDoorPolicyMotionSensorStatePickerView } from './edit-door-policy/EditDoorPolicyMotionSensorStatePicker';
import { EditDoorPolicyFlapStatePickerView } from './edit-door-policy/EditDoorPolicyFlapStatePicker';
import { EditDoorPolicyTimeRangePickerView } from './edit-door-policy/EditDoorPolicyTimeRangePicker';
import { EditDoorPolicyRfidPickerView } from './edit-door-policy/EditDoorPolicyRfidPicker';
import RfidChip from '../components/RfidChip';
import styles from './DoorPolicyModal.module.css';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import { formatDurationLabel, getClassificationLabel, getFlapStateLabel, getMotionSensorStateLabel, getTriggerSourceLabel } from '../utils/doorPolicyTranslations';
import { useDeveloperModeQuery } from '../query/developerModeQuery';

const DEFAULT_LOCKOUT_DURATION_MS = 30_000;

type RuleProp = TransitPolicyRule | undefined;
type RuleResolver = RuleProp | (() => RuleProp);

interface EditDoorPolicyRuleProps {
    dismiss: (data?: string | null | undefined | number, role?: string) => void;
    deviceId: string;
    onDelete: (ruleIndex?: number) => void;
    onSave: (rule: TransitPolicyRule, ruleIndex?: number) => void;
    rule?: RuleResolver;
    ruleIndex?: number;
}

const resolveRule = (rule?: RuleResolver) => {
    if (typeof rule === 'function') {
        return rule();
    }
    return rule;
};

const formatList = (values: Array<string | number>, orLabel = 'or') => {
    if (values.length === 1) {
        return `${values[0]}`;
    }
    return `${values.slice(0, -1).join(', ')} ${orLabel} ${values[values.length - 1]}`;
};

const toArray = <T,>(value?: T | T[]) => {
    if (value === undefined) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

const resolveLockActionOption = (action?: TransitPolicyRule['action']): LockActionOption => {
    if (!action) {
        return 'none';
    }
    if (action.lock === undefined) {
        if (action.lockoutDuration !== undefined || action.final) {
            return 'lock';
        }
        return 'none';
    }
    return action.lock ? 'lock' : 'unlock';
};

const resolveLockDurationOption = (action?: TransitPolicyRule['action']): LockDurationOption => {
    if (!action) {
        return 'default';
    }
    if (action.lockoutDuration !== undefined) {
        return 'for_duration';
    }
    if (action.final) {
        return 'until_event_end';
    }
    return 'default';
};

interface EditDoorPolicyRuleMainProps extends EditDoorPolicyRuleProps {
    navRef: RefObject<HTMLIonNavElement>;
}

const EditDoorPolicyRuleMain = ({ dismiss, rule, ruleIndex, navRef, deviceId, onSave, onDelete }: EditDoorPolicyRuleMainProps) => {
    const resolvedRule = resolveRule(rule);
    const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
    const [triggerSources, setTriggerSources] = useState<EventTriggerSource[]>(
        () => toArray(resolvedRule?.criteria?.eventTriggerSource)
    );
    const [classifications, setClassifications] = useState<EventClassification[]>(
        () => toArray(resolvedRule?.criteria?.eventClassification)
    );
    const [motionSensorStates, setMotionSensorStates] = useState<MotionSensorState[]>(
        () => toArray(resolvedRule?.criteria?.motionSensorState)
    );
    const [flapStates, setFlapStates] = useState<FlapState[]>(
        () => toArray(resolvedRule?.criteria?.flapState)
    );
    const [timeRangesInput, setTimeRangesInput] = useState(
        () => {
            const values = toArray(resolvedRule?.criteria?.timeRange);
            return values.length ? values.join(', ') : '';
        }
    );
    const [rfidCodesInput, setRfidCodesInput] = useState(
        () => {
            const values = toArray(resolvedRule?.criteria?.rfidCode);
            return values.length ? values.join(', ') : '';
        }
    );
    const [lockActionOption, setLockActionOption] = useState<LockActionOption>(() => resolveLockActionOption(resolvedRule?.action));
    const [lockDurationOption, setLockDurationOption] = useState<LockDurationOption>(() => resolveLockDurationOption(resolvedRule?.action));
    const [lockoutDurationMs, setLockoutDurationMs] = useState<number | undefined>(() => resolvedRule?.action?.lockoutDuration);
    const [soundInput, setSoundInput] = useState(() => resolvedRule?.action?.sound ?? '');
    const { t: tCommon } = useCommonTranslation();
    const { t: tModals } = useModalTranslation();
    const developerModeQuery = useDeveloperModeQuery();
    const developerMode = !!developerModeQuery.data;

    const toggleSelection = <T,>(current: T[], value: T, setter: (next: T[]) => void) => {
        if (current.includes(value)) {
            setter(current.filter((item) => item !== value));
        } else {
            setter([...current, value]);
        }
    };

    const triggerSourceText = triggerSources.length
        ? formatList(triggerSources.map((source) => getTriggerSourceLabel(tModals, source)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_trigger_source');
    const classificationText = classifications.length
        ? formatList(classifications.map((classification) => getClassificationLabel(tModals, classification)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_classification');
    const motionSensorStateText = motionSensorStates.length
        ? formatList(motionSensorStates.map((state) => getMotionSensorStateLabel(tModals, state)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_motion_sensor_state');
    const flapStateText = flapStates.length
        ? formatList(flapStates.map((state) => getFlapStateLabel(tModals, state)), tModals('door_policy.rule_summary.or'))
        : tModals('door_policy.edit_rule.any_flap_state');
    const timeRangeText = timeRangesInput.trim().length ? timeRangesInput : tModals('door_policy.edit_rule.any_time');
    const rfidCodesList = rfidCodesInput
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    const rfidCodesText = rfidCodesList.length ? '' : tModals('door_policy.edit_rule.any_microchip');
    const lockDurationLabel = formatDurationLabel(
        tModals,
        lockoutDurationMs && lockoutDurationMs > 0 ? lockoutDurationMs : DEFAULT_LOCKOUT_DURATION_MS,
        { short: true }
    );
    const lockActionText = (() => {
        switch (lockActionOption) {
            case 'none':
                return tModals('door_policy.edit_rule.no_lock_action');
            case 'unlock':
                return tModals('door_policy.edit_rule.unlock_the_door');
            case 'lock':
                if (lockDurationOption === 'until_event_end') {
                    return tModals('door_policy.edit_rule.lock_until_event_end');
                }
                if (lockDurationOption === 'for_duration') {
                    return tModals('door_policy.edit_rule.lock_for_duration', { duration: lockDurationLabel });
                }
                return tModals('door_policy.edit_rule.lock_the_door');
            default:
                return tModals('door_policy.edit_rule.no_lock_action');
        }
    })();
    const trimmedSoundInput = soundInput.trim();
    const soundLabel = getSoundLabel(tModals, trimmedSoundInput);
    const soundActionText = trimmedSoundInput.length
        ? tModals('door_policy.edit_rule.play_sound', { sound: soundLabel })
        : tModals('door_policy.edit_rule.no_sound');
    const isTriggerSourceSet = triggerSources.length > 0;
    const isClassificationSet = classifications.length > 0;
    const isMotionSensorStateSet = motionSensorStates.length > 0;
    const isFlapStateSet = flapStates.length > 0;
    const isTimeRangeSet = timeRangesInput.trim().length > 0;
    const isRfidSet = rfidCodesList.length > 0;
    const isLockActionSet = lockActionOption !== 'none';
    const isSoundSet = soundInput.trim().length > 0;
    const shouldShowMotionSensorState = developerMode || isMotionSensorStateSet;
    const shouldShowFlapState = developerMode || isFlapStateSet;

    const openSoundPicker = () => {
        navRef.current?.push(EditDoorPolicySoundPickerView, {
            soundInput,
            onSelectSound: setSoundInput,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openTriggerSourcePicker = () => {
        navRef.current?.push(EditDoorPolicyTriggerSourcePickerView, {
            triggerSources,
            onSelectTriggerSources: setTriggerSources,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openClassificationPicker = () => {
        navRef.current?.push(EditDoorPolicyClassificationPickerView, {
            classifications,
            onSelectClassifications: setClassifications,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openMotionSensorStatePicker = () => {
        navRef.current?.push(EditDoorPolicyMotionSensorStatePickerView, {
            motionSensorStates,
            onSelectMotionSensorStates: setMotionSensorStates,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openFlapStatePicker = () => {
        navRef.current?.push(EditDoorPolicyFlapStatePickerView, {
            flapStates,
            onSelectFlapStates: setFlapStates,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openTimeRangePicker = () => {
        navRef.current?.push(EditDoorPolicyTimeRangePickerView, {
            timeRange: timeRangesInput,
            onSelectTimeRange: setTimeRangesInput,
            onClose: () => navRef.current?.pop(),
        });
    };
    const openRfidPicker = () => {
        navRef.current?.push(EditDoorPolicyRfidPickerView, {
            deviceId,
            rfidCodes: rfidCodesList,
            onSelectRfidCodes: (codes: string[]) => setRfidCodesInput(codes.join(', ')),
            onClose: () => navRef.current?.pop(),
        });
    };

    const saveRule = () => {
        if (!resolvedRule) {
            dismiss(undefined, 'cancel');
            return;
        }

        const criteria: TransitPolicyRule['criteria'] = {};
        if (triggerSources.length) {
            criteria.eventTriggerSource = triggerSources;
        }
        if (classifications.length) {
            criteria.eventClassification = classifications;
        }
        if (motionSensorStates.length) {
            criteria.motionSensorState = motionSensorStates;
        }
        if (flapStates.length) {
            criteria.flapState = flapStates;
        }
        if (timeRangesInput.trim()) {
            criteria.timeRange = timeRangesInput.trim();
        }
        if (rfidCodesList.length) {
            criteria.rfidCode = rfidCodesList;
        }

        const action: TransitPolicyRule['action'] = {};
        switch (lockActionOption) {
            case 'unlock':
                action.lock = false;
                break;
            case 'lock':
                action.lock = true;
                if (lockDurationOption === 'until_event_end') {
                    action.final = true;
                } else if (lockDurationOption === 'for_duration') {
                    const durationMs = lockoutDurationMs && lockoutDurationMs > 0 ? lockoutDurationMs : DEFAULT_LOCKOUT_DURATION_MS;
                    action.lockoutDuration = durationMs;
                }
                break;
            case 'none':
            default:
                break;
        }
        if (soundInput.trim()) {
            action.sound = soundInput.trim();
        }

        const updatedRule = new TransitPolicyRule({
            ...resolvedRule,
            criteria: Object.keys(criteria).length ? criteria : undefined,
            action: Object.keys(action).length ? action : undefined,
        });

        onSave(updatedRule, ruleIndex);
        dismiss(undefined, 'confirm');
    };
    const openLockPicker = () => {
        navRef.current?.push(EditDoorPolicyLockPickerView, {
            lockActionOption,
            lockDurationOption,
            lockDurationMs: lockoutDurationMs,
            onSelectLock: (nextOption: LockActionOption, nextDurationOption: LockDurationOption, nextDurationMs?: number) => {
                setLockActionOption(nextOption);
                setLockDurationOption(nextDurationOption);
                if (nextOption === 'lock' && nextDurationOption === 'for_duration') {
                    setLockoutDurationMs(nextDurationMs ?? DEFAULT_LOCKOUT_DURATION_MS);
                } else {
                    setLockoutDurationMs(undefined);
                }
            },
            onClose: () => navRef.current?.pop(),
        });
    };

    interface RuleFieldRow {
        key: string;
        label: string;
        value: string;
        isSet: boolean;
        onClick: () => void;
        chips?: string[];
    }

    const whenRows: RuleFieldRow[] = [
        { key: 'triggerSource', label: tModals('door_policy.edit_rule.trigger_source'), value: triggerSourceText, isSet: isTriggerSourceSet, onClick: openTriggerSourcePicker },
        { key: 'classification', label: tModals('door_policy.edit_rule.classification'), value: classificationText, isSet: isClassificationSet, onClick: openClassificationPicker },
        { key: 'timeRange', label: tModals('door_policy.edit_rule.time_range'), value: timeRangeText, isSet: isTimeRangeSet, onClick: openTimeRangePicker },
        { key: 'microchips', label: tModals('door_policy.edit_rule.microchips'), value: rfidCodesText, isSet: isRfidSet, onClick: openRfidPicker, chips: rfidCodesList },
        ...(shouldShowMotionSensorState
            ? [{ key: 'motionSensor', label: tModals('door_policy.edit_rule.motion_sensor_state'), value: motionSensorStateText, isSet: isMotionSensorStateSet, onClick: openMotionSensorStatePicker }]
            : []),
        ...(shouldShowFlapState
            ? [{ key: 'flapState', label: tModals('door_policy.edit_rule.flap_state'), value: flapStateText, isSet: isFlapStateSet, onClick: openFlapStatePicker }]
            : []),
    ];
    const thenRows: RuleFieldRow[] = [
        { key: 'doorLock', label: tModals('door_policy.edit_rule.door_lock'), value: lockActionText, isSet: isLockActionSet, onClick: openLockPicker },
        { key: 'sound', label: tModals('door_policy.edit_rule.sound'), value: soundActionText, isSet: isSoundSet, onClick: openSoundPicker },
    ];

    const renderFieldRows = (rows: RuleFieldRow[]) => rows.map((row, index) => (
        <IonItem
            key={row.key}
            button
            detail
            lines={index === rows.length - 1 ? 'none' : undefined}
            onClick={row.onClick}
        >
            <IonLabel>{row.label}</IonLabel>
            {row.chips?.length ? (
                <div slot="end" className={styles.fieldChips}>
                    {row.chips.map((rfidCode) => (
                        <RfidChip key={rfidCode} rfidCode={rfidCode} deviceId={deviceId} />
                    ))}
                </div>
            ) : (
                <IonText slot="end" className={row.isSet ? styles.fieldValueSet : styles.fieldValue}>
                    {row.value}
                </IonText>
            )}
        </IonItem>
    ));

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={() => dismiss(undefined, 'cancel')}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    {/* ios title mode in both platforms: keeps the title centered
                        regardless of the differing Cancel/Save widths. (mode is
                        honored at runtime but missing from IonTitle's React
                        typings, hence the cast.) */}
                    <IonTitle {...({ mode: 'ios' } as object)}>
                        {ruleIndex !== undefined
                            ? tModals('door_policy.edit_rule.title_with_index', { index: ruleIndex + 1 })
                            : tModals('door_policy.edit_rule.title')}
                    </IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={saveRule} strong={true}>
                            {tCommon('buttons.save')}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                {!resolvedRule && (
                    <IonNote className="ion-padding">
                        {tModals('door_policy.edit_rule.not_found')}
                    </IonNote>
                )}
                {resolvedRule && (
                    <div className={styles.wrapper}>
                        <section className={styles.section}>
                            <h2 className={styles.sectionLabel}>{tModals('door_policy.sections.when_edit')}</h2>
                            <IonList className={styles.listCard}>
                                {renderFieldRows(whenRows)}
                            </IonList>
                        </section>

                        <section className={styles.section}>
                            <h2 className={styles.sectionLabel}>{tModals('door_policy.sections.then_edit')}</h2>
                            <IonList className={styles.listCard}>
                                {renderFieldRows(thenRows)}
                            </IonList>
                        </section>
                        {ruleIndex !== undefined && (
                            <IonButton
                                expand="block"
                                fill="solid"
                                color="danger"
                                className={styles.deleteRuleButton}
                                onClick={() => setIsDeleteAlertOpen(true)}
                            >
                                {tModals('door_policy.edit_rule.delete_button')}
                            </IonButton>
                        )}
                    </div>
                )}
                <IonAlert
                    isOpen={isDeleteAlertOpen}
                    onDidDismiss={() => setIsDeleteAlertOpen(false)}
                    header={tModals('door_policy.edit_rule.delete_confirm_title')}
                    buttons={[
                        {
                            text: tCommon('buttons.cancel'),
                            role: 'cancel',
                        },
                        {
                            text: tCommon('buttons.delete'),
                            role: 'destructive',
                            handler: () => {
                                onDelete(ruleIndex);
                                dismiss(undefined, 'confirm');
                            },
                        },
                    ]}
                />
            </IonContent>
        </IonPage>
    );
};

const EditDoorPolicyRuleMainView = (props: EditDoorPolicyRuleMainProps) => (
    <EditDoorPolicyRuleMain {...props} />
);

const EditDoorPolicyRule = ({ dismiss, rule, ruleIndex, deviceId, onSave, onDelete }: EditDoorPolicyRuleProps) => {
    const navRef = useRef<HTMLIonNavElement>(null);

    return (
        <IonNav
            ref={navRef}
            root={EditDoorPolicyRuleMainView}
            rootParams={{ dismiss, rule, ruleIndex, navRef, deviceId, onSave, onDelete }}
        />
    );
};

export default EditDoorPolicyRule;
